import type {
	ShakaPlayer,
	ShakaPlayerConfiguration
} from '~/composables/useHlsPlayer/types';

import { loadShaka } from '~/composables/useHlsPlayer/shakaLoader';

const BUFFER_BEHIND = 5;
const BUFFER_AHEAD = 15;
const REBUFFER_GOAL = 2;

/** Lighter settings for preloading adjacent videos */
const PRELOAD_BUFFER_AHEAD = 5;
const PRELOAD_BANDWIDTH_ESTIMATE = 500_000;
const PLAYBACK_BANDWIDTH_ESTIMATE = 1_000_000;

/**
 * Preload caps resolution to 720p so segments are ~3-4× smaller.
 * Active playback allows up to 1080p once ABR confirms enough bandwidth.
 */
const PRELOAD_MAX_HEIGHT = 720;
const PLAYBACK_MAX_HEIGHT = 1080;

export interface CreatePlayerOptions {
	/** Use lighter buffer/ABR settings for preloading (not actively playing) */
	isPreload?: boolean;
	/** Full config override — when provided, ignores isPreload and uses this config directly */
	config?: ShakaPlayerConfiguration;
}

function buildPlayerConfig(isPreload: boolean): ShakaPlayerConfiguration {
	return {
		streaming: {
			bufferingGoal: isPreload ? PRELOAD_BUFFER_AHEAD : BUFFER_AHEAD,
			rebufferingGoal: REBUFFER_GOAL,
			bufferBehind: BUFFER_BEHIND,
			retryParameters: {
				maxAttempts: 2,
				baseDelay: 500,
				backoffFactor: 1.5,
				timeout: 10000
			}
		},
		abr: {
			enabled: true,
			defaultBandwidthEstimate: isPreload
				? PRELOAD_BANDWIDTH_ESTIMATE
				: PLAYBACK_BANDWIDTH_ESTIMATE,
			switchInterval: 4,
			bandwidthUpgradeTarget: 0.85,
			bandwidthDowngradeTarget: 0.95,
			restrictions: {
				maxHeight: isPreload ? PRELOAD_MAX_HEIGHT : PLAYBACK_MAX_HEIGHT
			}
		}
	};
}

export async function createPlayer(
	videoUrl: string,
	videoElement: HTMLVideoElement,
	options: CreatePlayerOptions = {}
): Promise<ShakaPlayer> {
	const existing = (videoElement as HTMLVideoElement & { shaka?: ShakaPlayer })
		.shaka;
	if (existing) {
		await existing.destroy();
	}

	const shaka = await loadShaka();

	if (!shaka.Player.isBrowserSupported()) {
		throw new Error('Browser not supported');
	}

	const player = new shaka.Player() as ShakaPlayer;
	const config =
		options.config ?? buildPlayerConfig(options.isPreload ?? false);

	player.configure(config);

	await player.attach(videoElement);
	await player.load(videoUrl);

	(videoElement as HTMLVideoElement & { shaka?: any }).shaka = player;
	return player;
}

/**
 * Reconfigure a preloaded player for active playback.
 * Lifts resolution restrictions and increases buffer/bandwidth settings.
 */
export function configurePlayerForPlayback(player: ShakaPlayer): void {
	if (!player) return;

	player.configure(buildPlayerConfig(false));
}

export async function destroyPlayer(player: ShakaPlayer | null): Promise<void> {
	if (!player) return;

	try {
		// Unload first to immediately cancel all in-flight network requests
		// (segment downloads, manifest fetches, etc.)
		await player.unload();
	} catch {
		// Ignore unload errors
	}

	try {
		await player.destroy();
	} catch {
		// Ignore destroy errors
	}
}

export { clearVideoCache } from '~/composables/useHlsPlayer/shakaLoader';
